import { useFormik } from "formik";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { object, string } from "yup";
import ExternalRefIdHelpAdornment from "./ExternalRefIdHelpAdornment";
import { humanizeDateString } from "../../utilities/formatting";
import APIMonitor from "../../types/APIMonitor";
import { MonitoringFrequency } from "../../types/APIMonitoringFrequency";
import { useTrueBizApi } from "../../api";
import APICompanyMatchProfile from "../../types/APICompanyMatchProfile";

const MonitoringFormSchema = object({
  frequency: string().oneOf(Object.keys(MonitoringFrequency)).required(),
  profileId: string().nullable(),
  externalRefId: string().nullable(),
});
type FormState = ReturnType<typeof MonitoringFormSchema.validateSync>;

const getExplanation = (monitor: APIMonitor): string => {
  let explanation = `This monitor was created on ${humanizeDateString(
    monitor.created_at
  )}`;
  if (monitor.not_before !== null) {
    explanation += ` and is scheduled to run next on or around ${humanizeDateString(
      monitor.not_before
    )}`;
  }
  return explanation;
};

const DEFAULT_PROFILE_ID = "TRUEBIZ_defaultValue";

export default function MonitorForm({
  monitor,
  open,
  onClose,
  onSubmit,
}: {
  monitor: APIMonitor | null;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const api = useTrueBizApi();
  const [matchProfiles, setMatchProfiles] = useState<
    APICompanyMatchProfile[] | null
  >(null);
  const formik = useFormik({
    initialValues: {
      frequency: monitor ? monitor.frequency : MonitoringFrequency.MONTHLY,
      profileId:
        monitor && monitor.profile_id ? monitor.profile_id : DEFAULT_PROFILE_ID,
      externalRefId: monitor?.external_ref_id || "",
    },
    onSubmit: async (values: FormState) => {
      if (!monitor) return;

      await api.updateMonitor(monitor.id, {
        frequency: values.frequency,
        profile_id:
          values.profileId === DEFAULT_PROFILE_ID ? "" : values.profileId,
        domain: monitor.domain,
        monitor_type: monitor.monitor_type,
        external_ref_id: values.externalRefId || null,
      });

      onSubmit();
    },
    validationSchema: MonitoringFormSchema,
  });

  useEffect(() => {
    if (!open) return;

    const loadProfiles = async () => {
      setMatchProfiles(await api.getCompanyMatchProfiles());
    };

    loadProfiles();
  }, [api, setMatchProfiles, open]);

  return (
    <Dialog open={open}>
      <DialogTitle>{monitor ? monitor.name : "Create monitor"}</DialogTitle>
      <DialogContent>
        {monitor && (
          <DialogContentText>{getExplanation(monitor)}</DialogContentText>
        )}

        {matchProfiles === null ? (
          <CircularProgress size={28} sx={{ marginLeft: 1 }} />
        ) : (
          <Grid container spacing={2} mt={3} pl={2} pr={2}>
            <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
              <Grid item xs={12}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel id="frequency-label">Frequency</InputLabel>

                  <Select
                    labelId="frequency-label"
                    label="Frequency"
                    id="frequency-select"
                    onChange={formik.handleChange}
                    value={formik.values.frequency}
                    name="frequency"
                  >
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                    <MenuItem value="SEMI_ANNUALLY">Semiannually</MenuItem>
                    <MenuItem value="ANNUALLY">Annually</MenuItem>
                  </Select>

                  {formik.errors.frequency && formik.touched.frequency && (
                    <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                      {formik.errors.frequency}
                    </Alert>
                  )}
                </FormControl>
              </Grid>

              {matchProfiles && !!matchProfiles.length && (
                <Grid item xs={12} mt={3}>
                  <FormControl sx={{ width: "100%" }}>
                    <InputLabel id="match-profile-label">
                      Risk Profile
                    </InputLabel>

                    <Select
                      labelId="match-profile-label"
                      label="Risk Profile"
                      value={formik.values.profileId}
                      id="match-profile-select"
                      onChange={formik.handleChange}
                      name="profileId"
                    >
                      <MenuItem value={DEFAULT_PROFILE_ID}>
                        TrueBiz Default
                      </MenuItem>

                      {matchProfiles?.map((profile) => (
                        <MenuItem key={profile.id} value={profile.id}>
                          {profile.name}
                        </MenuItem>
                      ))}
                    </Select>

                    {formik.errors.profileId && formik.touched.profileId && (
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        {formik.errors.profileId}
                      </Alert>
                    )}
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} mt={3}>
                <FormControl sx={{ width: "100%" }}>
                  <TextField
                    label="External Reference Id"
                    id="external-ref-id-text-field"
                    onChange={formik.handleChange}
                    value={formik.values.externalRefId}
                    name="externalRefId"
                    InputProps={{
                      endAdornment: <ExternalRefIdHelpAdornment />,
                    }}
                  />
                  {formik.errors.externalRefId &&
                    formik.touched.externalRefId && (
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        {formik.errors.externalRefId}
                      </Alert>
                    )}
                </FormControl>
              </Grid>

              <Grid item xs={12} mt={3}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={onClose}
                      disabled={formik.isSubmitting}
                    >
                      Cancel
                    </Button>
                  </Box>

                  <Box sx={{ display: "flex" }}>
                    <Button
                      variant="contained"
                      disabled={formik.isSubmitting}
                      type="submit"
                    >
                      {monitor ? "Update" : "Create"}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </form>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}
